import React, { useState } from 'react';
import { Button, ButtonGroup, Col, Form, Modal, Row } from 'react-bootstrap';
import { Block } from '../Blocks/Block';
import UsernameInput from '../Form/UsernameInput/UsernameInput';
import { useCommunicationPreferences } from './useCommunicationPreferences';
import { useUsernamePreferences } from './useUsernamePreferences';

type Props = {
    username?: string;
    memberId?: string;
};

const Preferences: React.FC<Props> = ({ username, memberId }) => {
    const { models, operations } = useCommunicationPreferences();
    const { show, formData, anonymous } = models;
    const { handleOpen, handleClose, handleInputChangeEvent, onUpdate } = operations;

    const {
        models: uModels,
        operations: uOps,
    } = useUsernamePreferences({ initUsername: username, memberId });

    const { input, inProgress: usernameBusy, message: usernameMsg, dirty } = uModels;
    const { setInput, save: saveUsername } = uOps;

    const [showUsernameInput, setShowUsernameInput] = useState(false);

    const onClose = () => {
        handleClose();
        setShowUsernameInput(false);
    }
    const onSaveAll = async () => {
        const usernameResult = await (dirty ? saveUsername() : Promise.resolve({ ok: true }));
        if (!usernameResult.ok) return;
        await onUpdate();
        onClose();
    };

    return (
        <>
            {anonymous ? (
                show ? null : (
                    <p className="m-4">
                        Your communication preferences have been updated. <br />
                        You can now close this page.
                    </p>
                )
            ) : (
                <Button title="Update Preferences" variant="secondary" size="sm" onClick={handleOpen}>
                    <i className="bi bi-gear"></i>
                </Button>
            )}

            <Modal show={show} onHide={anonymous ? undefined : handleClose}>
                <Modal.Body>
                    {!anonymous && (
                        <Block title="Account Options">


                            {!showUsernameInput ? (<>
                                <p className="mb-2">Username</p>
                                <Form.Text >
                                    Changing your username will affect how you log in to Coalesce; please only change it if you are sure.
                                    Changes will be reflected when you log back in with your new username.
                                </Form.Text>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setShowUsernameInput(true)}
                                    className="mt-4"
                                >
                                    <i className="bi bi-pencil me-2"></i> {username}
                                </Button>
                            </>
                            ) : (
                                <Row className="align-items-center mt-2">
                                    <UsernameInput
                                        disabled={usernameBusy}
                                        required={false}
                                        check={dirty}
                                        value={input}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
                                        usernameTakenMessage="Username taken."
                                    />
                                    {usernameMsg && (
                                        <Form.Text className="text-danger mt-2">{usernameMsg}</Form.Text>
                                    )}
                                </Row>
                            )}

                        </Block>
                    )}

                    <Block title="Communication Preferences">
                        <p className="mb-4">
                            Please select the types of communication you would like to recieve.
                        </p>
                        <Row>
                            {formData.length > 0 &&
                                formData.map((preference, index) => (
                                    <Form.Group as={Col} key={index} sm={12} className="mb-3">
                                        <Form.Check
                                            type="switch"
                                            id={`switch-${preference.name}`}
                                            label={preference.name}
                                            checked={preference.enabled}
                                            onChange={() => handleInputChangeEvent(index)}
                                        />
                                    </Form.Group>
                                ))}

                            <Form.Group as={Col} sm={12} className="mb-3">
                                <Form.Check type="switch" label={'Essential Communications'} checked readOnly disabled />
                                <Form.Text>
                                    We occasionally need to send essential communications regarding
                                    your account and membership status. While these notifications
                                    cannot be disabled, please be assured that we will only contact
                                    you when it is necessary to provide critical information or
                                    updates.
                                </Form.Text>
                            </Form.Group>
                        </Row>
                    </Block>
                </Modal.Body>

                <Modal.Footer className="justify-content-center">
                    <ButtonGroup>
                        {!anonymous && (
                            <Button variant="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                        )}
                        <Button variant="primary" onClick={onSaveAll} disabled={usernameBusy}>
                            Update
                        </Button>
                    </ButtonGroup>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Preferences;
